import { useLingui } from '@lingui/react/macro';
import { Image } from '@/routes/-components/image';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { cn } from '@worldcoo/ui/lib/utils';

/**
 * Every position in this diagram is a PERCENTAGE of the 729x458 group the hand-off draws it in, so the
 * whole orbit scales as one piece instead of falling apart on a narrow screen. The export lists its
 * children in the SECTION's coordinates rather than the group's, so each value here is the export's
 * minus the group's own origin at (79, 131).
 */
const ORBITS = [
	// The three rings share a left tangent at x=127 and a common centre line, so they read as ripples
	// spreading right from the Delivery Hero node rather than as concentric circles.
	{ left: '6.6%', top: '6.1%', width: '93.4%', height: '93.9%' },
	{ left: '6.6%', top: '22.1%', width: '56.7%', height: '63.3%' },
	{ left: '6.6%', top: '37.8%', width: '20.2%', height: '31.9%' },
];

/**
 * The four photo bubbles: 56px on a 729px group, so 7.7% wide. Each keeps the plate colour the export
 * gives it, which is now what shows while the photograph loads rather than a stand-in for it.
 *
 * The photographs are matched to positions off the design, and the order matters — the bag belongs
 * upper-left against its blue wall, not in the park. Read visually, after comparing average colours
 * put two of them in each other's place: the design's crops are tight, so a crop of the yellow bag is
 * nothing like the average of a frame whose background is a dark blue wall.
 */
const BUBBLES = [
	// Each photograph is a SQUARE crop, cut around the measured centroid of its subject — the Glovo
	// yellow, the orange jacket, the green sleeve — rather than around the middle of the frame. Cutting
	// them square is what lets the markup below stay a plain cover fill: with no overflowing axis there
	// is no crop left to steer from CSS. A centre crop of the untouched files put the bag behind its
	// blue wall and left the park courier small under a canopy, neither of which is what the design shows.
	{ left: '26.7%', top: '17.2%', plate: 'bg-brand', src: '/redesign/about/glovo-bag.jpg' },
	{ left: '50.5%', top: '0%', plate: 'bg-ink', src: '/redesign/about/courier-handover.jpg' },
	{ left: '38.5%', top: '76.4%', plate: 'bg-olive', src: '/redesign/about/glovo-courier-park.jpg' },
	{ left: '83.0%', top: '79.3%', plate: 'bg-brand', src: '/redesign/about/courier-rain.jpg' },
];

/** A white 116px disc holding one of the three brands. */
function Node(props: { left: string; children: React.ReactNode; className?: string }) {
	return (
		<div
			style={{ left: props.left }}
			className={cn(
				'absolute top-[40.4%] flex h-[25.3%] w-[15.9%] items-center justify-center rounded-full bg-white',
				props.className,
			)}
		>
			{props.children}
		</div>
	);
}

/**
 * "Parte del ecosistema Delivery Hero" — the ownership band.
 *
 * A three-node orbit diagram on the left and the copy on the right. The copy comes FIRST in the DOM
 * because that is the reading order on a phone; `xl:` then places both where the hand-off puts them.
 */
export function DeliveryHero() {
	const { t } = useLingui();
	return (
		<section className="bg-linen relative overflow-hidden">
			<div className="relative mx-auto flex max-w-[1440px] flex-col gap-12 px-6 py-16 md:py-20 xl:h-[720px] xl:gap-0 xl:px-0 xl:py-0">
				{/* 443 wide at left 918, vertically centred. The copy stays FIRST in the DOM — it is the reading
				    order a screen reader wants — but the mobile hand-off puts the diagram above it, so the
				    diagram is re-ordered visually rather than moved. From `xl` both are absolute and order is
				    moot. */}
				<div className="order-2 flex w-full max-w-[443px] flex-col gap-2 xl:absolute xl:top-[256px] xl:left-[918px] xl:order-none">
					<h2 className="text-ink text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`Part of the Delivery Hero ecosystem`}
					</h2>
					{/* #000000 in the export. Every other body copy on a light band in this hand-off is #25302C
					    or a grey, so this may well be an oversight — it ships as drawn until the designers say. */}
					<p className="text-base leading-[1.5] text-black">
						{t`In 2023, Worldcoo was acquired by Delivery Hero and has been part of the Glovo family in Spain ever since. This partnership multiplies our technological and logistical capacity to take digital solidarity even further.`}
					</p>
				</div>

				{/* The 729x458 diagram at left 79, vertically centred. */}
				<div className="relative order-1 aspect-[729/458] w-full xl:absolute xl:top-[131px] xl:left-[79px] xl:order-none xl:w-[729px]">
					{ORBITS.map((orbit) => (
						<div key={orbit.width} aria-hidden style={orbit} className="border-ink/25 absolute rounded-[50%] border" />
					))}

					{/* 606px of dashed rule threading the three nodes, at 15% where the rings are at 25%. */}
					<div
						aria-hidden
						className="border-ink/15 absolute top-[53.7%] left-[8.1%] w-[83.1%] border-t border-dashed"
					/>

					{BUBBLES.map((bubble) => (
						<div
							key={bubble.src}
							style={{ left: bubble.left, top: bubble.top }}
							// Decorative: the paragraph beside the diagram already says what the partnership is, and
							// four couriers carry no information a reader would otherwise miss. So `alt=""` and no
							// msgid — four of those would cost forty translations to describe ornament.
							aria-hidden
							className={cn('absolute h-[12.2%] w-[7.7%] overflow-hidden rounded-full', bubble.plate)}
						>
							{/* `height={56}` is the bubble at `xl`, which is what anchors the srcset; the box itself
							    stays a percentage of the diagram, so it shrinks to 29px on a 375 screen. */}
							<Image
								src={bubble.src}
								alt=""
								height={56}
								aspectRatio={1}
								objectFit="cover"
								className="size-full object-cover!"
							/>
						</div>
					))}

					{/* Each mark takes its share of the 116px disc straight from the export: 87, 74 and 58. */}
					<Node left="0%">
						<img src="/redesign/about/delivery-hero.svg" alt="Delivery Hero" className="h-auto w-[75%]" />
					</Node>
					<Node left="45.0%">
						<img src="/redesign/about/glovo.svg" alt="Glovo" className="h-auto w-[63.8%]" />
					</Node>
					{/* The shared component rather than a fourth asset: it is the same stacked lockup the hand-off
					    draws (283.35x158.85 against 58x33 is the same 1.78 aspect) and header and footer already use it.
					    Its colours come from the export and are NOT the theme tokens: the wordmark is #262626, a
					    little lighter than `ink` (#25302C), while the accent is the brand orange. */}
					<Node left="81.3%">
						<WorldcooLogo className="h-auto w-1/2 text-[#262626]" accentClassName="text-brand" />
					</Node>
				</div>
			</div>
		</section>
	);
}
